/**
 * PublishConfirmModal (BON-215) — the pre-publish confirmation modal shown BEFORE
 * the publish endpoint is called. Built to the app's existing modal design system
 * (CrewPickerModal / CheckInModal): a centered `nb-card` over a scrim, closable via
 * the ×, Escape, scrim click, or Cancel; `role="dialog"` / `aria-modal`.
 *
 * It presents the human-readable summary an operator confirms before going live —
 * type title + description, date + duration, capacity, price, and the crew
 * (teacher / assistants / trainees, by resolved name) — and runs a CLIENT-SIDE
 * readiness mirror (computeReadiness) for fast feedback: if any blocker holds,
 * Confirm is disabled and the blockers are listed. The SERVER stays authoritative,
 * so a race that slips past the mirror comes back as a 409 whose `fieldErrors` are
 * surfaced here (via the `fieldErrors` prop) as a backstop.
 *
 * existing publish path (`onConfirm`); the page owns the mutation + refresh.
 */
import { useEffect } from 'react';

import type { WorkshopDetail } from '../../../../api/workshops';

import { Blockers } from './PublishModalBlockers';
import { Crew } from './PublishModalCrew';
import { Description } from './PublishModalDescription';
import { Footer } from './PublishModalFooter';
import { Header } from './PublishModalHeader';
import { Summary } from './PublishModalSummary';
import { Warnings } from './PublishModalWarnings';
import { computeReadiness } from './publishReadiness';

export interface PublishConfirmModalProps {
  workshop: WorkshopDetail;
  /** Current user holds `write:all` — overrides the 6-week lead in the mirror. */
  /** True while the publish mutation is in flight (disables Confirm + labels it). */
  publishing: boolean;
  /** Server 409 field errors (backstop for a race), keyed like the mirror's
   *  blockers; surfaced in the modal. Null when there's no server error. */
  fieldErrors: Record<string, string> | null;
  /** Injectable clock for deterministic lead-time math; defaults to now. */
  now?: Date;
  /** Invoke the publish path (the page owns the mutation + refresh). */
  onConfirm: () => void | Promise<void>;
  /** Close without changes (Cancel / Escape / scrim / ×). */
  onClose: () => void;
}

/** The crew members for one position, in insertion order. */

export function PublishConfirmModal({
  workshop,
  publishing,
  fieldErrors,
  now,
  onConfirm,
  onClose,
}: PublishConfirmModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = workshop.workshopType.displayName ?? workshop.workshopType.name;
  const description = workshop.workshopType.description;
  const readiness = computeReadiness({ workshop, now });
  const serverErrors = fieldErrors ? Object.values(fieldErrors) : [];
  const canConfirm = readiness.ready && !publishing;

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: 12,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Publish workshop"
        onClick={(e) => e.stopPropagation()}
        className="nb-card"
        style={{
          width: 460,
          maxWidth: '92vw',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <Header title={title} onClose={onClose} />
        <div style={{ overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {description ? <Description text={description} /> : null}
          <Summary workshop={workshop} />
          <Crew staff={workshop.staff} />
          <Blockers messages={[...readiness.blockers.map((b) => b.message), ...serverErrors]} />
          <Warnings messages={readiness.warnings} />
        </div>
        <Footer
          canConfirm={canConfirm}
          publishing={publishing}
          onConfirm={onConfirm}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
